import type { GameEvent } from '@cribbage/engine';

/**
 * Every sound in the game, synthesised.
 *
 * Thirteen one-shots, no binary assets: a cribbage table makes small, dry,
 * short noises — a card sliding on felt, a peg going into a hole — and those
 * are cheaper and more consistent to build out of filtered noise and a decaying
 * oscillator than to ship, decode, and keep in sync with an atlas of samples.
 *
 * The peg clack is the one that matters. A scoring run is read out beat by beat
 * ("fifteen two, fifteen four, and a pair for six") and the peg hops once per
 * beat; pitching each hop a whole tone above the last is what makes a run *feel*
 * like a run rather than three unrelated ticks. `playFor` owns that state, so
 * anything wired to the engine's event stream gets it for free — including the
 * two ornaments that mark a hand worth looking up from the table for.
 *
 * The render peaks, cue levels and fixed category trims define the sound mix.
 * One master gain lets the player turn all sounds up or down together.
 */

export type SoundName =
  | 'cardSwish'
  | 'flipSnap'
  | 'pegClack'
  | 'feltThud'
  | 'go'
  | 'countReset'
  | 'winFlourish'
  | 'loseFlourish'
  | 'skunkStamp'
  | 'selectTick'
  | 'timerWarn'
  | 'emotePop'
  | 'error';

/**
 * What a cue is *for*, which is what decides how loud it gets to be.
 *
 * `ui` is feedback on the player's own finger and should sit under everything;
 * `table` is the game making its ordinary noises; `score` is the game telling
 * you something happened, and is the only category that plays at full level.
 */
export type SoundCategory = 'ui' | 'table' | 'score';

/**
 * The mix as designed — the balance between the categories, which is ours to
 * decide and not the player's. The overall volume scales the whole mix.
 */
export const CATEGORY_TRIM: Readonly<Record<SoundCategory, number>> = {
  ui: 0.4,
  table: 0.7,
  score: 1,
};

interface SoundSpec {
  readonly category: SoundCategory;
  /** The cue's own level, before its category trim. */
  readonly level: number;
  /**
   * Whether repeats detune slightly. A twelve-card deal firing the identical
   * swish twelve times reads as a machine gun rather than as twelve cards.
   */
  readonly varies: boolean;
}

/**
 * The mix, and — because it is keyed by `SoundName` — the list of sounds that
 * exist. A cue added to the union without a line here is a compile error.
 */
export const SOUND_MIX: Readonly<Record<SoundName, SoundSpec>> = {
  cardSwish: { category: 'table', level: 1, varies: true },
  flipSnap: { category: 'table', level: 1, varies: false },
  pegClack: { category: 'score', level: 1, varies: false },
  feltThud: { category: 'table', level: 1, varies: true },
  go: { category: 'table', level: 1, varies: false },
  countReset: { category: 'table', level: 1, varies: false },
  winFlourish: { category: 'score', level: 1, varies: false },
  loseFlourish: { category: 'score', level: 0.7, varies: false },
  skunkStamp: { category: 'score', level: 1, varies: false },
  selectTick: { category: 'ui', level: 0.35, varies: false },
  timerWarn: { category: 'ui', level: 0.4, varies: false },
  emotePop: { category: 'ui', level: 0.45, varies: false },
  error: { category: 'ui', level: 1, varies: false },
};

export const SOUND_NAMES: readonly SoundName[] = Object.keys(SOUND_MIX) as SoundName[];

export interface PlayOptions {
  /** Frequency multiplier; 1 is the sound's natural pitch. */
  readonly pitch?: number | undefined;
  /** Overrides the cue's own level in [0, 1]. The category trim still applies. */
  readonly gain?: number | undefined;
}

/** What `play` actually scheduled — returned so callers (and tests) can see it. */
export interface PlayedCue {
  readonly sound: SoundName;
  readonly pitch: number;
  /** Level after the category trim: what the render peaks are scaled by. */
  readonly gain: number;
  readonly category: SoundCategory;
}

export interface AudioBusOptions {
  readonly muted?: boolean | undefined;
  /**
   * Full-scale master gain — what the player's slider means at 100%. Headroom
   * matters: a dozen one-shots can land in one frame.
   */
  readonly volume?: number | undefined;
  /**
   * Injected so tests can drive a mock graph and so a headless build never
   * touches WebAudio at all.
   */
  readonly createContext?: (() => AudioContext) | undefined;
  /**
   * Pitch jitter. Presentation-only and deliberately outside the game's seeded
   * RNG — but injectable, because "the same swish twice" is a thing to assert.
   */
  readonly random?: (() => number) | undefined;
  /** Wall clock for the retrigger guard, in milliseconds. */
  readonly now?: (() => number) | undefined;
}

/** A whole tone per hop — a run should climb a scale, not a chromatic smear. */
const PEG_PITCH_STEP = 1.122;
/** A run longer than this keeps climbing in pitch but stops sounding musical. */
const PEG_PITCH_MAX_STEPS = 7;
/** Points in one item worth an octave shimmer over the clack. */
const SHIMMER_POINTS = 6;
/** Double pair royal. Nothing else in the deck scores twelve at once. */
const FLICK_POINTS = 12;
/** Hand totals that earn the flick outright. 28 and 29 are once-a-lifetime. */
const FLICK_HAND_TOTALS: readonly number[] = [28, 29];
/** Detune depth for repeated cues. */
const PITCH_VARIANCE = 0.03;
/**
 * The same cue twice inside this window is a mistake somewhere upstream — two
 * screens reacting to one event, a re-render, a double tap — and it sounds like
 * a flam rather than like two of anything.
 */
const RETRIGGER_MS = 60;

export class AudioBus {
  private readonly create: () => AudioContext;
  private readonly random: () => number;
  private readonly clock: () => number;
  /** Full scale: what the master slider means at 1. */
  private readonly volume: number;
  /** The master slider itself, 0–1. */
  private level = 1;
  private readonly lastPlayed = new Map<SoundName, number>();
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private muted: boolean;
  private unavailable = false;
  private pegStep = 0;
  private bigHand = false;
  private flicked = false;
  private warnedToken: string | null = null;

  constructor(options: AudioBusOptions = {}) {
    this.create = options.createContext ?? defaultContext;
    this.random = options.random ?? Math.random;
    this.clock = options.now ?? defaultNow;
    this.volume = options.volume ?? 0.55;
    this.muted = options.muted ?? false;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** True once a context exists — i.e. once a gesture has woken the bus. */
  get isReady(): boolean {
    return this.ctx !== null;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
  }

  /**
   * The master slider, 0–1. Applied to the one gain node every cue passes
   * through, so a drag is heard by sounds already decaying rather than only by
   * the next one — which is what makes a volume slider feel like a volume
   * slider and not like a setting.
   */
  setVolume(level: number): void {
    this.level = clamp(level, 0, 1);
    if (this.master !== null) this.master.gain.value = this.volume * this.level;
  }

  /**
   * Call from a real user gesture. Browsers refuse to start an `AudioContext`
   * outside one, and iOS in particular only truly opens the output once
   * something has been scheduled from inside the gesture — hence the silent
   * frame rather than a bare `resume()`.
   */
  unlock(): void {
    const ctx = this.context();
    if (ctx === null) return;
    this.resume(ctx);
    try {
      const source = ctx.createBufferSource();
      source.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      source.connect(ctx.destination);
      source.start(ctx.currentTime);
    } catch {
      /* an unlock that fails costs nothing; the next gesture tries again */
    }
  }

  /**
   * A recorded voice, a members' emote's (`table/talk/premium.ts`). It is a
   * file, not a synthesized cue, so it plays through an audio element rather
   * than the graph, but it obeys the same mute and slider. Returns whether it
   * started. A browser that refuses playback (no gesture yet) costs nothing.
   */
  voice(url: string): boolean {
    if (this.muted || this.level === 0) return false;
    if (typeof globalThis.Audio !== 'function') return false;
    try {
      const audio = new Audio(url);
      audio.volume = clamp(this.volume * this.level, 0, 1);
      void audio.play()?.catch(() => undefined);
      return true;
    } catch {
      return false;
    }
  }

  play(sound: SoundName, options: PlayOptions = {}): PlayedCue | null {
    if (this.muted) return null;
    // A slider at zero is off, not merely quiet: scheduling a silent envelope
    // would churn the graph once per card for no one's benefit.
    if (this.level === 0) return null;
    const at = this.clock();
    const last = this.lastPlayed.get(sound);
    if (last !== undefined && at - last < RETRIGGER_MS) return null;

    const ctx = this.context();
    const master = this.master;
    if (ctx === null || master === null) return null;
    // Covers the iOS case where the silent switch, a phone call, or a spell in
    // the background left the context suspended long after it was unlocked.
    this.resume(ctx);

    const spec = SOUND_MIX[sound];
    const cue: PlayedCue = {
      sound,
      pitch: clamp(this.varied(spec, options.pitch ?? 1), 0.25, 4),
      gain: clamp((options.gain ?? spec.level) * CATEGORY_TRIM[spec.category], 0, 1),
      category: spec.category,
    };
    try {
      this.render(ctx, master, cue);
    } catch {
      return null;
    }
    this.lastPlayed.set(sound, at);
    return cue;
  }

  /**
   * The table wires this straight to the event stream. Consecutive
   * `POINTS_SCORED` events form one scoring run and climb in pitch; anything
   * else ends the run.
   */
  playFor(event: GameEvent): PlayedCue | null {
    const sound = soundFor(event);
    if (event.type !== 'POINTS_SCORED') {
      // A 28 or a 29 is announced before it is pegged, so latch it here and let
      // the run that follows wear the ornament.
      this.bigHand =
        (event.type === 'HAND_SHOWN' || event.type === 'CRIB_SHOWN') &&
        FLICK_HAND_TOTALS.includes(event.total);
      this.pegStep = 0;
      this.flicked = false;
      // A rematch is the one moment a caller's timer token can legitimately
      // repeat — same hand number, same seat, new game.
      if (event.type === 'GAME_STARTED') this.warnedToken = null;
      return sound === null ? null : this.play(sound);
    }

    const pitch = PEG_PITCH_STEP ** Math.min(this.pegStep, PEG_PITCH_MAX_STEPS);
    this.pegStep += 1;
    if (sound === null) return null;

    const cue = this.play(sound, { pitch });
    if (cue === null) return null;
    const { points } = event.item;
    if (points >= SHIMMER_POINTS) this.ornament('shimmer', cue);
    // Once per run: a 29 hand both announces itself and contains a twelve, and
    // two flicks in three seconds is a fanfare rather than a flourish.
    if (!this.flicked && (points >= FLICK_POINTS || this.bigHand)) {
      this.ornament('flick', cue);
      this.flicked = true;
    }
    return cue;
  }

  /** The card under the finger. `selected` false is the put-it-back variant. */
  selectTick(selected: boolean): PlayedCue | null {
    return this.play('selectTick', { pitch: selected ? 1 : 0.85 });
  }

  /**
   * The clock is running out. `token` identifies the turn being warned about —
   * pass the same one every tick and the warning lands exactly once, which is
   * what a per-frame timer needs and what a re-render must not defeat. Anything
   * that changes when the turn does will do (`${handNumber}:${seat}`); a
   * `GAME_STARTED` clears the memory, so a rematch may reuse its tokens.
   */
  timerWarn(token: string): PlayedCue | null {
    if (this.warnedToken === token) return null;
    this.warnedToken = token;
    return this.play('timerWarn');
  }

  /** Start the next scoring run from the bottom of the ladder. */
  resetPegRun(): void {
    this.pegStep = 0;
    this.bigHand = false;
    this.flicked = false;
  }

  dispose(): void {
    const ctx = this.ctx;
    this.ctx = null;
    this.master = null;
    this.noise = null;
    this.unavailable = true;
    this.lastPlayed.clear();
    if (ctx === null) return;
    void ctx.close().catch(() => {
      /* the page is going away regardless */
    });
  }

  /* ---------------------------------------------------------------- context */

  private context(): AudioContext | null {
    if (this.ctx !== null) return this.ctx;
    if (this.unavailable) return null;
    try {
      const ctx = this.create();
      const master = ctx.createGain();
      master.gain.value = this.volume * this.level;
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      return ctx;
    } catch {
      // No WebAudio (old WebView, SSR, a locked-down embed): stay silent
      // forever rather than throwing on every event.
      this.unavailable = true;
      return null;
    }
  }

  private resume(ctx: AudioContext): void {
    if (ctx.state === 'running') return;
    void ctx.resume().catch(() => {
      /* still gestureless; the next tap gets another go */
    });
  }

  private varied(spec: SoundSpec, pitch: number): number {
    if (!spec.varies) return pitch;
    return pitch * (1 + (this.random() * 2 - 1) * PITCH_VARIANCE);
  }

  /**
   * Deterministic white noise. A fresh random buffer per load makes the swish
   * audibly different between sessions, which is the sort of thing you only
   * notice as "the sound is wrong today".
   */
  private noiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noise !== null) return this.noise;
    const frames = Math.max(1, Math.floor(ctx.sampleRate * 0.4));
    const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let seed = 0x9e37_79b9;
    for (let i = 0; i < frames; i++) {
      seed = (Math.imul(seed, 1_664_525) + 1_013_904_223) >>> 0;
      data[i] = (seed / 0x1_0000_0000) * 2 - 1;
    }
    this.noise = buffer;
    return buffer;
  }

  /* --------------------------------------------------------------- synthesis */

  /**
   * The two decorations a scoring run can wear. They are not cues of their own:
   * they ride a clack that has already passed the mute, context, and retrigger
   * checks, which is why they take the cue rather than a sound name.
   */
  private ornament(kind: 'shimmer' | 'flick', cue: PlayedCue): void {
    const ctx = this.ctx;
    const master = this.master;
    if (ctx === null || master === null) return;
    const now = ctx.currentTime;
    const { pitch, gain } = cue;

    try {
      if (kind === 'shimmer') {
        // One octave over the clack's body, quiet enough to be a colour rather
        // than a second sound.
        this.tone(ctx, master, {
          type: 'triangle',
          from: 1240 * pitch,
          start: now,
          attack: 0.006,
          decay: 0.26,
          peak: 0.07 * gain,
        });
        return;
      }
      // C–D–G, three degrees of a pentatonic scale: the only figure in the game
      // that sounds like applause.
      [1046.5, 1174.66, 1567.98].forEach((frequency, index) => {
        this.tone(ctx, master, {
          type: 'triangle',
          from: frequency * pitch,
          start: now + index * 0.045,
          attack: 0.004,
          decay: 0.18,
          peak: 0.12 * gain,
        });
      });
    } catch {
      /* an ornament that fails to schedule is not worth losing the clack over */
    }
  }

  private render(ctx: AudioContext, master: GainNode, cue: PlayedCue): void {
    const now = ctx.currentTime;
    const { pitch, gain } = cue;

    switch (cue.sound) {
      case 'cardSwish':
        // Broadband noise sweeping upward: paper accelerating across felt.
        this.noiseHit(ctx, master, {
          start: now,
          duration: 0.18,
          filter: 'bandpass',
          from: 600 * pitch,
          to: 2400 * pitch,
          q: 0.9,
          peak: 0.22 * gain,
        });
        break;

      case 'flipSnap':
        this.noiseHit(ctx, master, {
          start: now,
          duration: 0.05,
          filter: 'highpass',
          from: 2200 * pitch,
          q: 0.7,
          peak: 0.3 * gain,
        });
        this.tone(ctx, master, {
          type: 'triangle',
          from: 940 * pitch,
          to: 620 * pitch,
          start: now,
          attack: 0.002,
          decay: 0.05,
          peak: 0.1 * gain,
        });
        break;

      case 'pegClack':
        // Brass into wood: a bright transient over a very short pitched body.
        this.noiseHit(ctx, master, {
          start: now,
          duration: 0.03,
          filter: 'highpass',
          from: 3000 * pitch,
          q: 0.6,
          peak: 0.16 * gain,
        });
        this.tone(ctx, master, {
          type: 'triangle',
          from: 620 * pitch,
          to: 380 * pitch,
          start: now,
          attack: 0.002,
          decay: 0.09,
          peak: 0.28 * gain,
        });
        break;

      case 'feltThud':
        this.tone(ctx, master, {
          type: 'sine',
          from: 150 * pitch,
          to: 70 * pitch,
          start: now,
          attack: 0.004,
          decay: 0.16,
          peak: 0.3 * gain,
        });
        this.noiseHit(ctx, master, {
          start: now,
          duration: 0.09,
          filter: 'lowpass',
          from: 420 * pitch,
          q: 0.5,
          peak: 0.12 * gain,
        });
        break;

      case 'go':
        // Two descending blips — a shrug, not a buzzer.
        this.tone(ctx, master, {
          type: 'square',
          from: 440 * pitch,
          start: now,
          attack: 0.005,
          decay: 0.09,
          peak: 0.14 * gain,
        });
        this.tone(ctx, master, {
          type: 'square',
          from: 330 * pitch,
          start: now + 0.11,
          attack: 0.005,
          decay: 0.11,
          peak: 0.14 * gain,
        });
        break;

      case 'countReset':
        this.noiseHit(ctx, master, {
          start: now,
          duration: 0.28,
          filter: 'bandpass',
          from: 1800 * pitch,
          to: 380 * pitch,
          q: 1.1,
          peak: 0.14 * gain,
        });
        this.tone(ctx, master, {
          type: 'sine',
          from: 300 * pitch,
          to: 150 * pitch,
          start: now,
          attack: 0.01,
          decay: 0.25,
          peak: 0.12 * gain,
        });
        break;

      case 'winFlourish':
        this.arpeggio(ctx, master, [523.25, 659.25, 783.99, 1046.5], pitch, gain);
        break;

      case 'loseFlourish':
        // The same rhythm falling instead of rising: the shape of the win, read
        // backwards. Nobody needs to be told twice that they lost.
        this.arpeggio(ctx, master, [659.25, 523.25, 440], pitch, gain);
        break;

      case 'skunkStamp':
        // The press of the stamp, then the ink: a thud under a minor third
        // dropping away from it.
        this.tone(ctx, master, {
          type: 'sine',
          from: 70 * pitch,
          start: now,
          attack: 0.003,
          decay: 0.2,
          peak: 0.42 * gain,
        });
        this.tone(ctx, master, {
          type: 'square',
          from: 330 * pitch,
          to: 277.18 * pitch,
          start: now,
          attack: 0.004,
          decay: 0.196,
          peak: 0.16 * gain,
        });
        break;

      case 'selectTick':
        // Eight milliseconds of 1.6kHz. Any longer and picking four cards for
        // the crib turns into typing.
        this.noiseHit(ctx, master, {
          start: now,
          duration: 0.008,
          filter: 'bandpass',
          from: 1600 * pitch,
          q: 4,
          peak: 0.5 * gain,
        });
        break;

      case 'timerWarn':
        // Two soft blips at the same pitch: a nudge, not an alarm. The ring is
        // already turning red; this is only for eyes that are elsewhere.
        this.tone(ctx, master, {
          type: 'sine',
          from: 660 * pitch,
          start: now,
          attack: 0.008,
          decay: 0.1,
          peak: 0.5 * gain,
        });
        this.tone(ctx, master, {
          type: 'sine',
          from: 660 * pitch,
          start: now + 0.16,
          attack: 0.008,
          decay: 0.1,
          peak: 0.5 * gain,
        });
        break;

      case 'emotePop':
        this.tone(ctx, master, {
          type: 'sine',
          from: 520 * pitch,
          to: 660 * pitch,
          start: now,
          attack: 0.01,
          decay: 0.11,
          peak: 0.5 * gain,
        });
        break;

      case 'error':
        this.tone(ctx, master, {
          type: 'square',
          from: 180 * pitch,
          start: now,
          attack: 0.004,
          decay: 0.1,
          peak: 0.16 * gain,
        });
        this.tone(ctx, master, {
          type: 'square',
          from: 140 * pitch,
          start: now + 0.09,
          attack: 0.004,
          decay: 0.12,
          peak: 0.16 * gain,
        });
        break;
    }
  }

  private arpeggio(
    ctx: AudioContext,
    dest: AudioNode,
    frequencies: readonly number[],
    pitch: number,
    gain: number,
  ): void {
    const now = ctx.currentTime;
    frequencies.forEach((frequency, index) => {
      this.tone(ctx, dest, {
        type: 'triangle',
        from: frequency * pitch,
        start: now + index * 0.11,
        attack: 0.012,
        decay: 0.34,
        peak: 0.2 * gain,
      });
    });
  }

  private tone(
    ctx: AudioContext,
    dest: AudioNode,
    options: {
      readonly type: OscillatorType;
      readonly from: number;
      readonly to?: number | undefined;
      readonly start: number;
      readonly attack: number;
      readonly decay: number;
      readonly peak: number;
    },
  ): void {
    const end = options.start + options.attack + options.decay;
    const osc = ctx.createOscillator();
    osc.type = options.type;
    osc.frequency.setValueAtTime(options.from, options.start);
    if (options.to !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, options.to), end);
    }
    const env = envelope(ctx, options.start, options.attack, options.decay, options.peak);
    osc.connect(env);
    env.connect(dest);
    osc.start(options.start);
    osc.stop(end + 0.05);
  }

  private noiseHit(
    ctx: AudioContext,
    dest: AudioNode,
    options: {
      readonly start: number;
      readonly duration: number;
      readonly filter: BiquadFilterType;
      readonly from: number;
      readonly to?: number | undefined;
      readonly q: number;
      readonly peak: number;
    },
  ): void {
    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(ctx);
    const band = ctx.createBiquadFilter();
    band.type = options.filter;
    band.Q.value = options.q;
    band.frequency.setValueAtTime(options.from, options.start);
    if (options.to !== undefined) {
      band.frequency.exponentialRampToValueAtTime(
        Math.max(1, options.to),
        options.start + options.duration,
      );
    }
    const env = envelope(
      ctx,
      options.start,
      options.duration * 0.12,
      options.duration * 0.88,
      options.peak,
    );
    source.connect(band);
    band.connect(env);
    env.connect(dest);
    source.start(options.start);
    source.stop(options.start + options.duration + 0.05);
  }
}

/**
 * Which sound an engine event asks for. Pure and exported so the mapping can be
 * asserted without a graph, and so the table can decide to suppress a sound
 * (during a fast-forward, say) without duplicating it.
 *
 * `GAME_ENDED` answers `winFlourish` because that is the mapping's best guess
 * with no viewer in hand; the one caller that knows who was watching (the job
 * factory) picks the flourish itself and never asks.
 *
 * `error` is deliberately absent: nothing the engine emits is a mistake. It is
 * played by the UI when a tap is rejected before a move is ever submitted.
 */
export function soundFor(event: GameEvent): SoundName | null {
  switch (event.type) {
    case 'CARDS_DEALT':
    case 'DISCARDED':
    case 'CRIB_SEEDED':
    case 'HAND_SHOWN':
    case 'LINE_COMPLETED':
      return 'cardSwish';

    case 'CUT_FOR_DEAL':
    case 'STARTER_CUT':
    case 'CARD_DRAWN':
    case 'CRIB_SHOWN':
      return 'flipSnap';

    case 'CARD_PLAYED':
    case 'CARD_PLACED':
      return 'feltThud';

    case 'GO_DECLARED':
      return 'go';

    case 'POINTS_SCORED':
      return 'pegClack';

    case 'COUNT_RESET':
    case 'GRID_COMPLETED':
      return 'countReset';

    case 'GAME_ENDED':
      return 'winFlourish';

    // The draw for who plays alone lands with the draw for the deal, which
    // already snaps: one sound for one moment.
    case 'ROLES_DRAWN':
    case 'GAME_STARTED':
    case 'HAND_STARTED':
    case 'ALL_DISCARDED':
    case 'PEGGING_ENDED':
    case 'PHASE_CHANGED':
      return null;

    default:
      return silent(event);
  }
}

/** A new event type is a compile error here rather than a silent gap. */
function silent(event: never): null {
  void event;
  return null;
}

function envelope(
  ctx: AudioContext,
  start: number,
  attack: number,
  decay: number,
  peak: number,
): GainNode {
  const gain = ctx.createGain();
  // Exponential ramps cannot touch zero, and the floor doubles as the silence
  // the one-shot decays into.
  const floor = 0.0001;
  const level = Math.max(floor * 2, peak);
  const rise = Math.max(0.001, attack);
  gain.gain.setValueAtTime(floor, start);
  gain.gain.exponentialRampToValueAtTime(level, start + rise);
  gain.gain.exponentialRampToValueAtTime(floor, start + rise + Math.max(0.001, decay));
  return gain;
}

function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(high, Math.max(low, value));
}

function defaultContext(): AudioContext {
  const ctor =
    globalThis.AudioContext ??
    (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (ctor === undefined) throw new Error('WebAudio is unavailable');
  return new ctor({ latencyHint: 'interactive' });
}

function defaultNow(): number {
  return globalThis.performance?.now() ?? Date.now();
}

/** The bus the app uses. Inert — and contextless — until something plays. */
export const audioBus = new AudioBus();
